import { createContext, type MutableRefObject, useContext, useEffect, useId, useRef } from "react";
import toast from "react-hot-toast";

import { useMutation } from "@tanstack/react-query";
import { Button } from "../../ui";
import { ModalActionsRow } from "../../components/ModalActionsRow";

export const ModalContainer = ({
  children,
  onClose,
  cancelLabel,
  saveLabel,
  hideCancel,
  hideSave,
  cancelButtonType = "cancel",
}: {
  children: React.ReactNode;
  onClose: () => void;
  cancelLabel?: string;
  saveLabel?: string;
  hideCancel?: boolean;
  hideSave?: boolean;
  cancelButtonType?: "cancel" | "closePrimary" | "closeSecondary";
}) => {
  const actionsRef = useRef({
    onSave: new Map<string, () => void>(),
    onCancel: new Map<string, () => void>(),
  });
  const onCancel = useMutation({
    mutationFn: async () => {
      const items = Array.from(actionsRef.current.onCancel.values());
      for (const onCancel of items) await onCancel();
    },
    onSuccess: () => onClose(),
  });
  const onSave = useMutation({
    mutationFn: async () => {
      const items = Array.from(actionsRef.current.onSave.values());
      for (const onSave of items) await onSave();
    },
    onSuccess: () => onClose(),
    onError: (error) => toast.error(error.message),
  });
  return (
    <ModalContext.Provider value={actionsRef}>
      <div className="h-full grid [grid-template-rows:1fr_max-content] pt-3 min-h-0 select-none [&_input]:select-text [&_textarea]:select-text">
        <div className="overflow-y-auto overflow-x-hidden">{children}</div>
        <ModalActionsRow className="p-4 border-t border-outline">
          {!hideCancel && (
            <Button
              buttonType={cancelButtonType}
              type="button"
              data-testid="modal-cancel"
              onClick={() => onCancel.mutate()}
            >
              {cancelLabel || "Cancel"}
            </Button>
          )}
          {!hideSave && (
            <Button buttonType="save" type="submit" data-testid="modal-save" onClick={() => onSave.mutate()}>
              {saveLabel || "Save"}
            </Button>
          )}
        </ModalActionsRow>
      </div>
    </ModalContext.Provider>
  );
};

const ModalContext = createContext<
  MutableRefObject<{
    onSave: Map<string, () => void>;
    onCancel: Map<string, () => void>;
  }>
>({ current: { onCancel: new Map(), onSave: new Map() } });

export const useOnSave = (f: () => Promise<unknown>) => {
  const id = useId();
  const actionsRef = useContext(ModalContext);
  useEffect(() => {
    actionsRef.current.onSave.set(id, f);
    return () => void actionsRef.current.onSave.delete(id);
  });
};

export const useOnCancel = (f: () => void) => {
  const id = useId();
  const actionsRef = useContext(ModalContext);
  useEffect(() => {
    actionsRef.current.onCancel.set(id, f);
    return () => void actionsRef.current.onCancel.delete(id);
  });
};

export const useAddAction = () => {
  const actionsRef = useContext(ModalContext);
  const id = useId();
  return {
    hasSave: () => actionsRef.current.onSave.has(id),
    hasCancel: () => actionsRef.current.onCancel.has(id),
    onSave: (f: () => void) => actionsRef.current.onSave.set(id, f),
    onCancel: (f: () => void) => actionsRef.current.onCancel.set(id, f),
  };
};
